import { ActionTree, ActionContext } from 'vuex';
import { RootState } from '@/store';
import { State } from './state';
import { Mutations } from './mutations';
import { HistoryMutationTypes } from './mutation-types';
import { HistoryActionTypes } from './action-types';
import {
  PortfolioHistoryApiOptions,
  HistoryItemResponse,
  ScenarioHistoryApiOptions,
  OpportunityHistoryApiOptions,
  HistoryListResponseItem,
} from './types';

import ServerAPI, {
  combineQueryParams,
  handleError,
} from '@/services/serverAPI';

type AugmentedAction = {
  commit<K extends keyof Mutations>(
    key: K,
    payload: Parameters<Mutations[K]>[1]
  ): ReturnType<Mutations[K]>;
} & Omit<ActionContext<State, RootState>, 'commit'>;

export interface Actions {
  [HistoryActionTypes.GET_PORTFOLIO_HISTORY](
    { commit }: AugmentedAction,
    payload: PortfolioHistoryApiOptions
  ): Promise<void>;
  [HistoryActionTypes.GET_SCENARIO_HISTORY](
    { commit }: AugmentedAction,
    payload: ScenarioHistoryApiOptions
  ): Promise<void>;
  [HistoryActionTypes.GET_OPPORTUNITY_HISTORY](
    { commit }: AugmentedAction,
    payload: OpportunityHistoryApiOptions
  ): Promise<void>;
}

export const actions: ActionTree<State, RootState> & Actions = {
  async [HistoryActionTypes.GET_PORTFOLIO_HISTORY](
    { commit },
    portfolioApiOptions: PortfolioHistoryApiOptions
  ) {
    try {
      const paramString = combineQueryParams({
        ['include_opportunities']: portfolioApiOptions.includeOpportunities,
        ['include_scenarios']: portfolioApiOptions.includeScenarios,
        ['include_completed']: portfolioApiOptions.includeCompleted,
      });

      const response: Array<HistoryItemResponse> = await ServerAPI.get(
        `/portfolios/${portfolioApiOptions.portfolioId}/history${paramString}`
      );
      commit(HistoryMutationTypes.SET_PORTFOLIO_HISTORY, response);
    } catch (e) {
      handleError(e);
      commit(HistoryMutationTypes.SET_PORTFOLIO_HISTORY, []);
    }
  },
  async [HistoryActionTypes.GET_SCENARIO_HISTORY](
    { commit },
    scenarioApiOptions: ScenarioHistoryApiOptions
  ) {
    try {
      const response: Array<HistoryListResponseItem> = await ServerAPI.get(
        `/scenario/${scenarioApiOptions.scenarioId}/history`
      );

      commit(HistoryMutationTypes.SET_HISTORY_LIST, {
        id: scenarioApiOptions.scenarioId,
        data: response,
      });
    } catch (e) {
      handleError(e);
    }
  },
  async [HistoryActionTypes.GET_OPPORTUNITY_HISTORY](
    { commit },
    opportunityApiOptions: OpportunityHistoryApiOptions
  ) {
    try {
      const response: Array<HistoryListResponseItem> = await ServerAPI.get(
        `/opportunities/${opportunityApiOptions.opportunityId}/history`
      );

      commit(HistoryMutationTypes.SET_HISTORY_LIST, {
        id: opportunityApiOptions.opportunityId,
        data: response,
      });
    } catch (e) {
      handleError(e);
    }
  },
};
